<template>
  <div class="w-full max-w-full min-w-0 overflow-x-hidden" dir="rtl">
    <div v-if="isAuthorized">
      <!-- Header + filters -->
      <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mb-4 w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
            <div class="min-w-0 overflow-hidden">
              <h3 class="text-sm font-bold text-gray-100 truncate">تسویه حساب</h3>
              <p class="text-[11px] text-gray-400 mt-1 truncate">
                طلب شریک از بابت فروش، منهای پرداختی‌های تاییدشده، و مانده‌حساب
              </p>
            </div>

            <button
                @click="fetchData"
                :disabled="loading"
                class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-200 hover:bg-gray-700 transition disabled:opacity-50 shrink-0"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              بروزرسانی
            </button>
          </div>
        </header>

        <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 min-w-0">
            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">از تاریخ</label>
              <date placeholder="از تاریخ" v-model="fromDate" />
            </div>

            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">تا تاریخ</label>
              <date placeholder="تا تاریخ" v-model="toDate" />
            </div>

            <div class="col-span-2 flex items-end gap-2 min-w-0">
              <button @click="applyFilter" class="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs text-white transition shrink-0">
                اعمال فیلتر
              </button>
              <button v-if="fromDate || toDate" @click="clearFilter" class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:bg-gray-700 transition shrink-0">
                پاک کردن (کل تاریخچه)
              </button>
            </div>
          </div>
        </section>

        <!-- Summary cards -->
        <section v-if="data" class="px-3 sm:px-4 py-4 grid grid-cols-1 lg:grid-cols-4 gap-2 sm:gap-3 min-w-0">
          <div class="rounded-xl bg-amber-600/10 border border-amber-500/20 px-4 py-3 min-w-0 overflow-hidden">
            <p class="text-[11px] text-amber-300/80 truncate">طلب شریک (مجموع فروش)</p>
            <p class="text-lg font-bold text-amber-300 mt-1 truncate" dir="ltr">{{ formatMoney(data.total_owed) }}</p>
          </div>
          <div class="rounded-xl bg-blue-600/10 border border-blue-500/20 px-4 py-3 min-w-0 overflow-hidden">
            <p class="text-[11px] text-blue-300/80 truncate">پرداختی تاییدشده (ملاک تسویه)</p>
            <p class="text-lg font-bold text-blue-300 mt-1 truncate" dir="ltr">{{ formatMoney(data.total_paid_confirmed) }}</p>
          </div>
          <div class="rounded-xl bg-gray-900/70 border border-gray-700 px-4 py-3 min-w-0 overflow-hidden">
            <p class="text-[11px] text-gray-400 truncate">مجموع کل پرداختی (تاییدشده + تاییدنشده)</p>
            <p class="text-lg font-bold text-gray-200 mt-1 truncate" dir="ltr">{{ formatMoney(data.total_paid_all) }}</p>
            <p v-if="unconfirmedAmount > 0" class="text-[10px] text-amber-400 mt-1 truncate">
              {{ formatMoney(unconfirmedAmount) }} از این مبلغ هنوز تایید نشده
            </p>
          </div>
          <div
              class="rounded-xl px-4 py-3 min-w-0 overflow-hidden border"
              :class="balanceIsOwed ? 'bg-rose-600/10 border-rose-500/20' : 'bg-emerald-600/10 border-emerald-500/20'"
          >
            <p class="text-[11px] truncate" :class="balanceIsOwed ? 'text-rose-300/80' : 'text-emerald-300/80'">
              {{ balanceIsOwed ? 'مانده بدهی به شریک' : 'پرداختی بیش از طلب (بستانکار)' }}
            </p>
            <p class="text-lg font-bold mt-1 truncate" dir="ltr" :class="balanceIsOwed ? 'text-rose-300' : 'text-emerald-300'">
              {{ formatMoney(Math.abs(data.balance)) }}
            </p>
            <p class="text-[10px] text-gray-500 mt-1 truncate">بر مبنای پرداختی‌های تاییدشده</p>
          </div>
        </section>

        <!-- Returns card (part of overall accounting) -->
        <section v-if="data" class="px-3 sm:px-4 pb-4 min-w-0">
          <button
              @click="goToReturns"
              class="w-full text-right rounded-xl bg-gray-900/70 border border-gray-700 hover:border-rose-500/50 px-4 py-3 min-w-0 overflow-hidden transition flex items-center justify-between gap-3"
          >
            <div class="min-w-0">
              <p class="text-[11px] text-gray-400 truncate">
                مرجوعی حضوری (از طلب شریک کسر شده) — {{ formatNumber(data.total_returns_quantity) }} عدد
              </p>
              <p class="text-sm font-bold text-rose-300 mt-1 truncate" dir="ltr">{{ formatMoney(data.total_returns_amount) }}</p>
            </div>
            <span class="shrink-0 text-[11px] text-gray-400 flex items-center gap-1">
              مشاهده‌ی لیست کامل <i class="fas fa-chevron-left"></i>
            </span>
          </button>
        </section>

        <!-- Chart -->
        <section v-if="data" class="px-3 sm:px-4 pb-4 min-w-0">
          <div class="bg-gray-900/50 border border-gray-700 rounded-xl p-3 min-w-0">
            <h4 class="text-[11px] font-bold text-gray-300 mb-2">طلب در برابر پرداختی</h4>
            <div class="relative h-56"><canvas ref="balanceChart"></canvas></div>
          </div>
        </section>
      </div>

      <!-- Loading / error -->
      <div v-if="loading" class="p-10 text-center">
        <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
        <p class="text-gray-400 mt-3 text-sm">در حال دریافت اطلاعات...</p>
      </div>

      <div v-else-if="error" class="p-10 text-center">
        <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
        <p class="text-red-400 mt-3 text-sm">{{ error }}</p>
        <button @click="fetchData" class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-white text-xs transition">
          تلاش مجدد
        </button>
      </div>

      <!-- Payments ledger -->
      <div v-else class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-3 bg-gray-900/50 border-b border-gray-700">
          <h4 class="text-xs font-bold text-gray-200">ریز پرداختی‌ها ({{ formatNumber(payments.length) }})</h4>
        </header>

        <!-- Mobile cards -->
        <div class="md:hidden p-3 space-y-3 w-full max-w-full min-w-0 overflow-hidden">
          <article
              v-for="p in payments"
              :key="p.type + '-' + p.id"
              class="rounded-2xl bg-gray-900/45 border border-gray-700/80 p-3 shadow-sm w-full max-w-full min-w-0 overflow-hidden"
          >
            <div class="flex items-center justify-between gap-2 min-w-0">
              <div class="flex items-center gap-1.5 min-w-0">
                <span :class="p.type === 'cash' ? 'bg-emerald-600/15 text-emerald-300 border-emerald-500/30' : 'bg-amber-600/15 text-amber-300 border-amber-500/30'" class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border">
                  <i :class="p.type === 'cash' ? 'fas fa-money-bill-wave' : 'fas fa-money-check'"></i>
                  {{ p.type === 'cash' ? 'نقدی' : 'چک' }}
                </span>
                <span
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border"
                    :class="p.is_confirmed ? 'bg-emerald-600/15 text-emerald-300 border-emerald-500/30' : 'bg-gray-600/15 text-gray-400 border-gray-500/30'"
                >
                  <i :class="p.is_confirmed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                  {{ p.is_confirmed ? 'تاییدشده' : 'تاییدنشده' }}
                </span>
              </div>
              <span class="text-[10px] text-gray-500">{{ p.date | toJalali }}</span>
            </div>
            <p class="text-sm font-bold text-gray-100 mt-2" dir="ltr">{{ formatMoney(p.amount) }}</p>
            <p v-if="p.note" class="text-[10px] text-gray-500 mt-2 pt-2 border-t border-gray-700/70">{{ p.note }}</p>
          </article>

          <div v-if="payments.length === 0" class="px-4 py-12 text-center rounded-2xl bg-gray-900/40 border border-gray-700">
            <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
            <p class="text-gray-400 text-sm">پرداختی‌ای ثبت نشده</p>
          </div>
        </div>

        <!-- Desktop table -->
        <div class="hidden md:block w-full max-w-full min-w-0 overflow-x-auto table-scroll-safe">
          <table class="w-full min-w-[720px] text-sm">
            <thead class="bg-gray-900/70 border-b border-gray-700">
            <tr>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">نوع</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">وضعیت</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تاریخ</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">مبلغ</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">توضیحات</th>
            </tr>
            </thead>

            <tbody class="divide-y divide-gray-700/50">
            <tr v-for="p in payments" :key="p.type + '-' + p.id" class="hover:bg-gray-700/30 transition">
              <td class="px-4 py-3 text-xs">
                <span :class="p.type === 'cash' ? 'bg-emerald-600/15 text-emerald-300 border-emerald-500/30' : 'bg-amber-600/15 text-amber-300 border-amber-500/30'" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border">
                  <i :class="p.type === 'cash' ? 'fas fa-money-bill-wave' : 'fas fa-money-check'"></i>
                  {{ p.type === 'cash' ? 'نقدی' : 'چک' }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs">
                <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
                    :class="p.is_confirmed ? 'bg-emerald-600/15 text-emerald-300 border-emerald-500/30' : 'bg-gray-600/15 text-gray-400 border-gray-500/30'"
                >
                  <i :class="p.is_confirmed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                  {{ p.is_confirmed ? 'تاییدشده' : 'تاییدنشده' }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{{ p.date | toJalali }}</td>
              <td class="px-4 py-3 text-xs text-gray-200 font-bold whitespace-nowrap text-right" dir="ltr">{{ formatMoney(p.amount) }}</td>
              <td class="px-4 py-3 text-xs text-gray-400 max-w-[320px]">
                <span class="block truncate">{{ p.note || '-' }}</span>
              </td>
            </tr>

            <tr v-if="payments.length === 0">
              <td colspan="5" class="px-4 py-12 text-center">
                <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
                <p class="text-gray-400 text-sm">پرداختی‌ای ثبت نشده</p>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else class="w-full rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-5 text-right">
      <h3 class="text-sm font-bold text-red-100">دسترسی غیرمجاز</h3>
      <p class="mt-2 text-xs text-red-200">دسترسی برای شما مجاز نیست.</p>
    </div>
  </div>
</template>

<script>
import getPartnerCalculationsApi from './getPartnerCalculationsApi'
import date from "@/components/mcomponents/cleave/Date"

export default {
  name: 'PartnerCalculationsSettlement',

  mixins: [getPartnerCalculationsApi],

  components: { date },

  props: {
    partnerId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      error: null,

      fromDate: '',
      toDate: '',

      data: null,

      balanceChart: null,
    }
  },

  computed: {
    isAdminMode() {
      return !!this.partnerId
    },
    isAuthorized() {
      if (this.isAdminMode) return this.user.user_type === 'ma' || this.user.is_superuser
      return this.user.user_type === 'prtnr' || this.user.user_type === 'ma'
    },
    payments() {
      return (this.data && this.data.payments) || []
    },
    balanceIsOwed() {
      return this.data && Number(this.data.balance) > 0
    },
    unconfirmedAmount() {
      if (!this.data) return 0
      return Number(this.data.total_paid_all || 0) - Number(this.data.total_paid_confirmed || 0)
    },
  },

  watch: {
    partnerId() {
      this.fetchData()
    },
  },

  methods: {
    goToReturns() {
      if (this.isAdminMode) {
        this.$emit('go-to-returns')
        return
      }
      this.$router.push({ name: 'PartnerCalculationsReturns' })
    },

    getParams() {
      const params = {}
      if (this.fromDate) params.from = this.fromDate
      if (this.toDate) params.to = this.toDate
      return params
    },

    fetchData() {
      this.loading = true
      this.error = null

      const onSuccess = data => {
        this.data = data
        this.loading = false
        this.$nextTick(() => this.renderChart())
      }
      const onError = () => {
        this.error = 'خطا در دریافت اطلاعات'
        this.loading = false
      }

      if (this.isAdminMode) {
        this.getAdminPartnerCalculationsSettlement(this.partnerId, this.getParams(), onSuccess, onError)
      } else {
        this.getPartnerCalculationsSettlement(this.getParams(), onSuccess, onError)
      }
    },

    applyFilter() {
      this.fetchData()
    },

    clearFilter() {
      this.fromDate = ''
      this.toDate = ''
      this.fetchData()
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'
      const number = Number(value)
      if (Number.isNaN(number)) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    renderChart() {
      if (typeof Chart === 'undefined') {
        console.error('Chart.js not loaded! Make sure to include Chart.js v2.9.4')
        return
      }

      const ctx = this.$refs.balanceChart
      if (!ctx || !this.data) return

      if (this.balanceChart) this.balanceChart.destroy()

      this.balanceChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['طلب شریک', 'پرداخت‌شده (تاییدشده)', 'پرداخت‌شده (تاییدنشده)'],
          datasets: [{
            data: [
              Number(this.data.total_owed || 0),
              Number(this.data.total_paid_confirmed || 0),
              this.unconfirmedAmount,
            ],
            backgroundColor: ['#f59e0b', '#3b82f6', '#6b7280'],
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: {
            rtl: true,
            callbacks: {
              label: (item) => this.formatMoney(item.yLabel)
            }
          },
          scales: {
            xAxes: [{
              ticks: { fontColor: '#9ca3af', fontSize: 10, fontFamily: 'Vazir, sans-serif' },
              gridLines: { display: false }
            }],
            yAxes: [{
              ticks: {
                beginAtZero: true,
                fontColor: '#9ca3af',
                fontSize: 9,
                callback: value => this.formatNumber(value)
              },
              gridLines: { color: 'rgba(75, 85, 99, 0.2)' }
            }]
          }
        }
      })
    },
  },

  mounted() {
    this.fetchData()
  },

  beforeDestroy() {
    if (this.balanceChart) this.balanceChart.destroy()
  }
}
</script>

<style scoped>
table {
  border-collapse: collapse;
}

.table-scroll-safe {
  scrollbar-width: thin;
}

.min-w-0 {
  min-width: 0 !important;
}
</style>
